import { createAsyncThunk } from '@reduxjs/toolkit';
import translation from 'translation';
import { apiGet, apiRemove, apiPost, apiPut } from 'api-service';
import { setCurrentQuery, setQueries } from 'store/page/pageSlice';
import { applyDefaultSubjectToCurrentQuery } from 'store/settings/settingsHelpers';
import {
  fetchWebsiteSettings,
  filtersInitialState,
  setSelectedFilterValue,
  sortsInitialState,
  updateWebsiteSettings,
} from 'store/settings/settingsSlice';
import { magicNumbers, getFirstQueryIndex } from 'utils';
import { toast } from 'lib';
import { subjectAdapter, subjectDetailAdapter } from './adapters';

const { t } = translation;

export const updateDefaultSubjectInSettings = createAsyncThunk(
  'subjectReducer/updateDefaultSubjectInSettings',
  async ({ subjectId, shouldBeDefault }, thunkAPI) => {
    const { filters, sortBy } = thunkAPI.getState().settingsReducer;
    const oldDefaultSubject =
      filters.public.defaultSubjectSelectBox.selectedValue;
    const updatedSubjectId = shouldBeDefault
      ? subjectId
      : magicNumbers.SUBJECT_FILTER_DEFAULT_OPTION;
    try {
      if (filters === filtersInitialState && sortBy === sortsInitialState) {
        await thunkAPI.dispatch(
          fetchWebsiteSettings({ shouldApplyToOtherReducers: false })
        );
      }
      thunkAPI.dispatch(
        setSelectedFilterValue({
          value: updatedSubjectId,
          componentName: 'defaultSubjectSelectBox',
          parentPlatform: 'public',
        })
      );

      const { isSuccessful } = await thunkAPI
        .dispatch(updateWebsiteSettings({ shouldApplyToOtherReducers: false }))
        .unwrap();
      if (isSuccessful) {
        applyDefaultSubjectToCurrentQuery({
          defaultSubjectSelectBox: { selectedValue: updatedSubjectId },
          thunkAPI,
        });
      }
      return { isSuccessful: true };
    } catch (error) {
      thunkAPI.dispatch(
        setSelectedFilterValue({
          value: oldDefaultSubject,
          componentName: 'defaultSubjectSelectBox',
          parentPlatform: 'public',
        })
      );
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchAllSubjects = createAsyncThunk(
  'subjectSlice/fetchSubjects',
  async (params, thunkAPI) => {
    const perPageSettings =
      thunkAPI.getState().settingsReducer?.filters?.public
        ?.pageContentCountSlider?.selectedValue;
    try {
      const response = await apiGet('/search/queries', {
        orderBy: [{ field: 'created_at', type: 'desc' }],
        per_page: perPageSettings,
        paginate: perPageSettings,
        page: 1,
        ...params,
      });
      return {
        subjects: response.data?.result?.data?.map((subject) =>
          subjectAdapter(subject)
        ),
        total: response.data?.result?.meta?.total,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchSubjectById = createAsyncThunk(
  'subjectSlice/fetchSubjectById',
  async ({ id }, thunkAPI) => {
    try {
      const response = await apiGet(`/search/queries/${id}`);
      return subjectDetailAdapter(response.data.result.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const editSubject = createAsyncThunk(
  'subjectSlice/editSubject',
  async ({ subjectId, payload }, thunkAPI) => {
    const queries = thunkAPI.getState().pageReducer.queries.data;
    try {
      const response = await apiPut(`/search/queries/${subjectId}`, payload);
      await thunkAPI.dispatch(
        setQueries(
          queries.map((query) => {
            if (parseInt(query.id) === parseInt(subjectId)) {
              return {
                ...query,
                parsed_query: payload.parsed_query,
                title: payload.title,
              };
            }
            return query;
          })
        )
      );

      toast({
        title: t('subject:success.updateDefaultSubject.title'),
        description: t('subject:success.updateQuery.description'),
        type: 'success',
      });

      return response.data?.result?.data;
    } catch (error) {
      const type = t('common:edit');
      if (typeof error?.response?.data?.errors?.data?.errors === 'object') {
        Object.entries(error?.response?.data?.errors?.data?.errors).forEach(
          (item) => {
            toast({
              title: t('subject:error.addOrEditQuery.title', {
                type,
              }),
              description: item[1][0].replace(
                'parsed_query',
                t('subject:error.addOrEditQuery.description')
              ),
              type: 'error',
            });
          }
        );
      } else if (
        typeof error?.response?.data?.errors?.data?.message === 'string'
      ) {
        toast({
          title: t('subject:error.addOrEditQuery.title', {
            type,
          }),
          description: error?.response?.data?.errors?.data?.message,
          type: 'error',
        });
      } else {
        toast({
          title: t('subject:error.addOrEditQuery.title', {
            type,
          }),
          description: t('subject:anErrorOccurred'),
          type: 'error',
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addSubject = createAsyncThunk(
  'subjectSlice/addSubject',
  async ({ payload }, thunkAPI) => {
    const queries = thunkAPI.getState().pageReducer.queries.data;
    try {
      const response = await apiPost('/search/queries', payload);
      const subjectId = response.data?.result?.data?.id;
      await thunkAPI.dispatch(
        setQueries([
          ...queries,
          {
            id: subjectId,
            parsed_query: payload.parsed_query,
            title: payload.title,
          },
        ])
      );
      if (queries.length === 0) {
        await thunkAPI.dispatch(
          setCurrentQuery({
            queryId: subjectId,
          })
        );
      }

      toast({
        title: t('subject:success.updateDefaultSubject.title'),
        description: t('subject:success.updateQuery.description'),
        type: 'success',
      });

      return { id: subjectId };
    } catch (error) {
      const type = t('common:save');
      if (typeof error?.response?.data?.errors?.data?.errors === 'object') {
        Object.entries(error?.response?.data?.errors?.data?.errors).forEach(
          (item) => {
            toast({
              title: t('subject:error.addOrEditQuery.title', {
                type,
              }),
              description: item[1][0].replace(
                'parsed_query',
                t('subject:error.addOrEditQuery.description')
              ),
              type: 'error',
            });
          }
        );
      } else if (
        typeof error?.response?.data?.errors?.data?.message === 'string'
      ) {
        toast({
          title: t('subject:error.addOrEditQuery.title', {
            type,
          }),
          description: error?.response?.data?.errors?.data?.message,
          type: 'error',
        });
      } else {
        toast({
          title: t('subject:error.addOrEditQuery.title', {
            type,
          }),
          description: t('subject:anErrorOccurred'),
          type: 'error',
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const removeSubject = createAsyncThunk(
  'subjectSlice/removeSubject',
  async (_, thunkAPI) => {
    const { selectedSubjectIds } = thunkAPI.getState().subjectReducer;
    const queries = thunkAPI.getState().pageReducer.queries.data;
    const filteredQueries = queries.filter(
      (query) => !selectedSubjectIds.includes(query.id)
    );
    try {
      await apiRemove(`/search/queries/${selectedSubjectIds.join(',')}`);
      await thunkAPI.dispatch(setQueries(filteredQueries));
      await thunkAPI.dispatch(
        setCurrentQuery({
          queryId: filteredQueries?.[getFirstQueryIndex(filteredQueries)]?.id,
        })
      );
      return { success: true };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
